import { ShaderModule, ShaderRenderContext } from './index';

/**
 * Particulate Void Shader Mode
 *
 * Simulates starship matter unthreading into an uncalculated mathematical void state.
 * Features:
 * - Exactly 2,000 unthreading quantum particles maintained in a single pre-allocated Float32Array
 * - Zero GC allocations per frame (particles recycled in-place)
 * - Divergence-free harmonic curl-noise vector field driving outward matter dissipation
 * - Brownian drift stochastic Wiener-process integration
 * - Quantum entanglement filaments connecting adjacent matter motes
 */
export class ParticulateVoidShader implements ShaderModule {
  readonly name = 'particulate_void' as const;

  static readonly PARTICLE_COUNT = 2000;
  // Stride of 8 floats per particle:
  // 0: x, 1: y, 2: vx, 3: vy, 4: size, 5: baseAlpha, 6: life (0..1), 7: maxLife (sec)
  private readonly particles: Float32Array;
  private initialized = false;
  private lastWidth = 0;
  private lastHeight = 0;

  constructor() {
    this.particles = new Float32Array(ParticulateVoidShader.PARTICLE_COUNT * 8);
  }

  getParticleCount(): number {
    return ParticulateVoidShader.PARTICLE_COUNT;
  }

  // Raw buffer accessor for unit testing assertions
  getParticleBuffer(): Float32Array {
    return this.particles;
  }

  private initParticles(width: number, height: number): void {
    const cx = width * 0.5;
    const cy = height * 0.5;

    for (let i = 0; i < ParticulateVoidShader.PARTICLE_COUNT; i++) {
      this.resetParticle(i, cx, cy, true);
    }
    this.initialized = true;
    this.lastWidth = width;
    this.lastHeight = height;
  }

  private resetParticle(index: number, cx: number, cy: number, randomizeLife = false): void {
    const offset = index * 8;
    // Radial unthreading distribution around center with initial dispersal
    const angle = Math.random() * Math.PI * 2;
    const radius = randomizeLife
      ? Math.random() * Math.min(cx, cy) * 0.95
      : Math.random() * 25 + 5;

    const px = cx + Math.cos(angle) * radius;
    const py = cy + Math.sin(angle) * radius;

    // Outward unthreading velocity vector
    const speed = 15 + Math.random() * 45;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    const size = 0.8 + Math.random() * 2.2;
    const baseAlpha = 0.25 + Math.random() * 0.65;
    const life = randomizeLife ? Math.random() : 0.0;
    const maxLife = 3.0 + Math.random() * 5.0; // 3 to 8 seconds

    this.particles[offset + 0] = px;
    this.particles[offset + 1] = py;
    this.particles[offset + 2] = vx;
    this.particles[offset + 3] = vy;
    this.particles[offset + 4] = size;
    this.particles[offset + 5] = baseAlpha;
    this.particles[offset + 6] = life;
    this.particles[offset + 7] = maxLife;
  }

  render(context: ShaderRenderContext): void {
    const { ctx, width, height, deltaTime, uniforms, time, reducedMotion } = context;
    const { speed, intensity, distortion, gridDensity } = uniforms;

    if (!this.initialized || width !== this.lastWidth || height !== this.lastHeight) {
      this.initParticles(width, height);
    }

    const dt = Math.min(deltaTime, 0.1); // Guard against giant delta jumps
    const cx = width * 0.5;
    const cy = height * 0.5;
    const animSpeed = reducedMotion ? speed * 0.3 : speed;
    const brownianScale = (reducedMotion ? 10 : 35) * intensity;
    const curlDistortion = distortion * 25;

    // 1. Semi-transparent void background for persistence trail
    ctx.fillStyle = '#05070a';
    ctx.fillRect(0, 0, width, height);

    // Deep void ambient core glow
    const coreGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(width, height) * 0.6);
    coreGrad.addColorStop(0, 'rgba(12, 18, 30, 0.5)');
    coreGrad.addColorStop(1, 'rgba(5, 7, 10, 0.98)');
    ctx.fillStyle = coreGrad;
    ctx.fillRect(0, 0, width, height);

    ctx.save();

    // 2. Particle Physics Integration & Rendering
    // Store positions of a subset of particles for quantum entanglement filaments
    const filamentStride = Math.max(12, Math.round(90 - gridDensity));
    const filamentSamples: Array<{ x: number; y: number; alpha: number }> = [];

    const sqrtDt = Math.sqrt(Math.max(dt, 0.001));

    for (let i = 0; i < ParticulateVoidShader.PARTICLE_COUNT; i++) {
      const offset = i * 8;
      let px = this.particles[offset + 0];
      let py = this.particles[offset + 1];
      let vx = this.particles[offset + 2];
      let vy = this.particles[offset + 3];
      const size = this.particles[offset + 4];
      const baseAlpha = this.particles[offset + 5];
      let life = this.particles[offset + 6];
      const maxLife = this.particles[offset + 7];

      // Update life cycle
      life += (dt * animSpeed) / maxLife;

      // Check boundary or expiration -> In-place recycle
      if (
        life >= 1.0 ||
        px < -60 ||
        px > width + 60 ||
        py < -60 ||
        py > height + 60
      ) {
        this.resetParticle(i, cx, cy, false);
        continue;
      }

      this.particles[offset + 6] = life;

      // Outward unthreading vector field
      const dx = px - cx;
      const dy = py - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const normDx = dx / dist;
      const normDy = dy / dist;

      // Divergence-free 2D Curl-noise flow modulation
      const curlX = -Math.sin(py * 0.006 + time * animSpeed * 0.7) * curlDistortion;
      const curlY = Math.cos(px * 0.006 - time * animSpeed * 0.7) * curlDistortion;

      // Brownian drift (Wiener process: normal stochastic jitter scaled by sqrt(dt))
      const brownianX = (Math.random() - 0.5) * brownianScale * sqrtDt * 10;
      const brownianY = (Math.random() - 0.5) * brownianScale * sqrtDt * 10;

      // Velocity integration
      const accelX = normDx * (10 + speed * 30) + curlX;
      const accelY = normDy * (10 + speed * 30) + curlY;

      vx += accelX * dt;
      vy += accelY * dt;

      // Drag damping
      vx *= 0.98;
      vy *= 0.98;

      px += (vx * animSpeed + brownianX) * dt;
      py += (vy * animSpeed + brownianY) * dt;

      this.particles[offset + 0] = px;
      this.particles[offset + 1] = py;
      this.particles[offset + 2] = vx;
      this.particles[offset + 3] = vy;

      // Matter dissipation curve: smooth sin curve over life
      const fadeProgress = Math.sin(life * Math.PI);
      const renderAlpha = Math.min(1, Math.max(0, baseAlpha * fadeProgress * (0.3 + intensity * 0.7)));

      if (renderAlpha < 0.01) continue;

      // Particle color palette: Cyan, Violet, or Bright Star White
      const colorType = i % 3;
      let fillStyle = '';
      if (colorType === 0) {
        fillStyle = `rgba(0, 243, 255, ${renderAlpha})`;
      } else if (colorType === 1) {
        fillStyle = `rgba(157, 78, 221, ${renderAlpha * 0.85})`;
      } else {
        fillStyle = `rgba(235, 248, 255, ${renderAlpha * 1.1})`;
      }

      ctx.fillStyle = fillStyle;
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();

      // Collect samples for quantum entanglement filaments
      if (i % filamentStride === 0 && renderAlpha > 0.15) {
        filamentSamples.push({ x: px, y: py, alpha: renderAlpha });
      }
    }

    // 3. Quantum Entanglement Filaments
    if (!reducedMotion && filamentSamples.length > 1) {
      const maxConnectDist = 45 + (gridDensity / 80) * 45;
      const maxConnectDistSq = maxConnectDist * maxConnectDist;
      ctx.lineWidth = 0.75;

      for (let a = 0; a < filamentSamples.length; a++) {
        const p1 = filamentSamples[a];
        for (let b = a + 1; b < filamentSamples.length; b++) {
          const p2 = filamentSamples[b];
          const fdx = p1.x - p2.x;
          const fdy = p1.y - p2.y;
          const distSq = fdx * fdx + fdy * fdy;

          if (distSq < maxConnectDistSq) {
            const filamentAlpha = (1.0 - Math.sqrt(distSq) / maxConnectDist) * Math.min(p1.alpha, p2.alpha) * 0.35 * intensity;
            if (filamentAlpha > 0.02) {
              ctx.strokeStyle = `rgba(0, 243, 255, ${filamentAlpha})`;
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          }
        }
      }
    }

    ctx.restore();
  }

  reset(): void {
    this.initialized = false;
  }
}
